import { Fragment, useEffect, useMemo, useState } from "react";
import { ChevronDown, ChevronRight, ChevronUp } from "lucide-react";
import { MESES_ABREV, formatCurrency, formatPercent } from "../../utils/orcamento";
import { thStyle, tdStyle } from "./DreGerencialView";
import { EMPRESAS_DRE, CATEGORIAS_RATEIO_EMPRESAS } from "../../constants/dreEmpresa";
import {
  getRateioEmpresas,
  putRateioEmpresa,
  getPrejuizoFiscalSaldo,
  putPrejuizoFiscalSaldo,
} from "../../api";

// DRE por Empresa: abre o DRE Gerencial (consolidado) por empresa jurídica (GT/CIGTA/CRIA).
// Faturamento Bruto (Backlog) e Impostos são "dado real" (o segundo, calculado pela cascata de
// imposto — ver backend/services/dre_empresa_svc.py); as demais linhas de custo/despesa ainda
// não são apuradas por empresa nos módulos de origem, então usam o percentual configurável no
// painel de rateio abaixo — cada uma exibe um selo verde ("dado real") ou laranja ("rateio X%").
// Renderer de linha próprio (não reaproveita LinhaRow de DreGerencialView.js) para não arriscar
// regressão nas telas de grupo — só thStyle/tdStyle são compartilhados.

const PADDING_POR_NIVEL = [10, 16, 32, 48];

const BG_POR_TIPO = (C) => ({
  grupo: C.categoriaBg,
  subgrupo: C.subcategoriaBg,
  conta: "transparent",
  derivado: C.bgActive,
  margem: C.margemBg,
});

function Selo({ linha, C }) {
  if (!linha.origem_dado) return null;
  const real = linha.origem_dado === "real";
  return (
    <span
      style={{
        display: "inline-block", fontSize: 9.5, fontWeight: 700, borderRadius: 999,
        padding: "1px 7px", marginLeft: 8, whiteSpace: "nowrap",
        background: real ? `${C.success}22` : "#F5972222",
        color: real ? C.success : "#F59722",
        border: `1px solid ${real ? C.success : "#F59722"}`,
      }}
    >
      {real ? "dado real" : `rateio ${Number(linha.rateio_pct).toFixed(0)}%`}
    </span>
  );
}

function EmpresaLinhaRow({ linha, nivel, colapsados, toggle, C }) {
  const temFilhos = Array.isArray(linha.filhos) && linha.filhos.length > 0;
  const colapsada = colapsados.has(linha.id);
  const bg = BG_POR_TIPO(C)[linha.tipo] || "transparent";
  const negrito = linha.tipo === "grupo" || linha.tipo === "derivado" || linha.tipo === "margem";
  const italico = linha.tipo === "margem";
  const valorFmt = (v) => (linha.unidade === "pct" ? formatPercent(v) : formatCurrency(v));

  return (
    <Fragment>
      <tr
        onClick={temFilhos ? () => toggle(linha.id) : undefined}
        style={{
          background: bg, fontWeight: negrito ? 700 : 400, fontStyle: italico ? "italic" : "normal",
          cursor: temFilhos ? "pointer" : "default",
          borderTop: linha.tipo === "derivado" ? `2px solid ${C.border}` : undefined,
        }}
      >
        <td style={tdStyle(C, { textAlign: "left" })}>
          <div style={{ display: "flex", alignItems: "center", gap: 6, paddingLeft: PADDING_POR_NIVEL[nivel] || 10 }}>
            {temFilhos ? (colapsada ? <ChevronRight size={13} /> : <ChevronDown size={13} />) : null}
            <span>{linha.label}</span>
            <Selo linha={linha} C={C} />
          </div>
        </td>
        {linha.meses.map((m) => (
          <td key={m.mes} style={tdStyle(C)}>{valorFmt(m.planejado)}</td>
        ))}
        <td style={tdStyle(C, { fontWeight: 700 })}>{valorFmt(linha.total.planejado)}</td>
      </tr>
      {temFilhos && !colapsada && linha.filhos.map((filho) => (
        <EmpresaLinhaRow key={filho.id} linha={filho} nivel={nivel + 1} colapsados={colapsados} toggle={toggle} C={C} />
      ))}
    </Fragment>
  );
}

function coletarIdsComFilhos(linhas) {
  const ids = [];
  for (const linha of linhas || []) {
    if (Array.isArray(linha.filhos) && linha.filhos.length > 0) {
      ids.push(linha.id);
      ids.push(...coletarIdsComFilhos(linha.filhos));
    }
  }
  return ids;
}

function KpiCard({ label, valor, sublinha, C }) {
  return (
    <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 12, padding: "12px 16px", minWidth: 170, flex: 1 }}>
      <div style={{ fontSize: 10, fontWeight: 700, color: C.muted, textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 6 }}>
        {label}
      </div>
      <div style={{ fontSize: 15, fontWeight: 800, color: C.text }}>{valor}</div>
      {sublinha && <div style={{ fontSize: 11, color: C.muted, marginTop: 2 }}>{sublinha}</div>}
    </div>
  );
}

const inputPctStyle = (C, valido) => ({
  width: 64, padding: "5px 6px", borderRadius: 6, fontSize: 12.5, textAlign: "right",
  border: `1px solid ${valido ? C.border : C.danger}`, background: C.bg, color: C.text,
});

function PainelRateio({ ano, C, onSalvo }) {
  const [aberto, setAberto] = useState(true);
  const [percentuais, setPercentuais] = useState({});
  const [saldoPrejuizo, setSaldoPrejuizo] = useState({});
  const [salvando, setSalvando] = useState(null);
  const [erro, setErro] = useState("");

  useEffect(() => {
    let cancelado = false;
    Promise.all([getRateioEmpresas(ano), getPrejuizoFiscalSaldo(ano)])
      .then(([rateio, prejuizo]) => {
        if (cancelado) return;
        const mapa = {};
        for (const c of rateio.categorias) mapa[c.categoria] = { ...c.percentuais };
        setPercentuais(mapa);
        const mapaPrejuizo = {};
        for (const p of prejuizo) mapaPrejuizo[p.empresa] = p.saldo_inicial;
        setSaldoPrejuizo(mapaPrejuizo);
      })
      .catch((e) => !cancelado && setErro(e.message));
    return () => { cancelado = true; };
  }, [ano]);

  function somaCategoria(categoria) {
    const valores = percentuais[categoria] || {};
    return EMPRESAS_DRE.reduce((acc, emp) => acc + (Number(valores[emp]) || 0), 0);
  }

  function atualizarPercentual(categoria, empresa, valor) {
    setPercentuais((prev) => ({ ...prev, [categoria]: { ...prev[categoria], [empresa]: valor } }));
  }

  async function salvarCategoria(categoria) {
    const soma = somaCategoria(categoria);
    if (Math.abs(soma - 100) > 0.01) return;
    setSalvando(categoria);
    setErro("");
    try {
      await putRateioEmpresa({
        categoria, ano_referencia: ano,
        percentuais: Object.fromEntries(EMPRESAS_DRE.map((emp) => [emp, Number(percentuais[categoria]?.[emp]) || 0])),
      });
      onSalvo();
    } catch (e) {
      setErro(e.message);
    } finally {
      setSalvando(null);
    }
  }

  async function salvarSaldoPrejuizo(empresa) {
    setSalvando(`prejuizo-${empresa}`);
    setErro("");
    try {
      await putPrejuizoFiscalSaldo({ empresa, ano_referencia: ano, saldo_inicial: Number(saldoPrejuizo[empresa]) || 0 });
      onSalvo();
    } catch (e) {
      setErro(e.message);
    } finally {
      setSalvando(null);
    }
  }

  return (
    <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 14, overflow: "hidden" }}>
      <div
        onClick={() => setAberto((v) => !v)}
        style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "12px 16px", cursor: "pointer" }}
      >
        <span style={{ fontSize: 13, fontWeight: 700, color: C.text }}>Painel de Rateio entre Empresas</span>
        {aberto ? <ChevronUp size={16} color={C.muted} /> : <ChevronDown size={16} color={C.muted} />}
      </div>
      {aberto && (
        <div style={{ padding: "0 16px 16px" }}>
          <div style={{ fontSize: 11.5, color: C.muted, marginBottom: 12, lineHeight: 1.5 }}>
            Faturamento Bruto (Backlog) e Impostos não entram aqui — o primeiro já é apurado por
            empresa, o segundo é calculado a partir do LAIR de cada empresa. As 7 categorias
            abaixo ainda não são apuradas por empresa nos módulos de origem — o percentual é uma
            aproximação; a soma entre GT/CIGTA/CRIA precisa ser exatamente 100% para salvar.
            Investimentos e Despesas Financeiras estão aqui como tratamento assumido, a confirmar.
          </div>
          {erro && (
            <div style={{ background: `${C.danger}18`, border: `1px solid ${C.danger}`, color: C.danger, borderRadius: 8, padding: "8px 12px", fontSize: 12, marginBottom: 12 }}>
              {erro}
            </div>
          )}
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr>
                  <th style={thStyle(C, { textAlign: "left" })}>Categoria</th>
                  {EMPRESAS_DRE.map((emp) => <th key={emp} style={thStyle(C)}>{emp}</th>)}
                  <th style={thStyle(C)}>Soma</th>
                  <th style={thStyle(C)}></th>
                </tr>
              </thead>
              <tbody>
                {CATEGORIAS_RATEIO_EMPRESAS.map(({ chave, label }) => {
                  const soma = somaCategoria(chave);
                  const valido = Math.abs(soma - 100) < 0.01;
                  return (
                    <tr key={chave}>
                      <td style={tdStyle(C, { textAlign: "left" })}>{label}</td>
                      {EMPRESAS_DRE.map((emp) => (
                        <td key={emp} style={tdStyle(C)}>
                          <input
                            type="number" step="0.01"
                            value={percentuais[chave]?.[emp] ?? ""}
                            onChange={(e) => atualizarPercentual(chave, emp, e.target.value)}
                            style={inputPctStyle(C, true)}
                          />
                        </td>
                      ))}
                      <td style={tdStyle(C, { fontWeight: 700, color: valido ? C.success : C.danger })}>
                        {soma.toFixed(2)}%
                      </td>
                      <td style={tdStyle(C)}>
                        <button
                          type="button" disabled={!valido || salvando === chave}
                          onClick={() => salvarCategoria(chave)}
                          style={{
                            background: "transparent", border: `1px solid ${C.brand}`, borderRadius: 6,
                            padding: "4px 10px", fontSize: 11, fontWeight: 600, color: C.brand,
                            cursor: valido ? "pointer" : "not-allowed", opacity: valido ? 1 : 0.4,
                          }}
                        >
                          {salvando === chave ? "Salvando…" : "Salvar"}
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div style={{ fontSize: 12, fontWeight: 700, color: C.text, margin: "16px 0 8px" }}>
            Saldo Inicial de Prejuízo Fiscal ({ano})
          </div>
          <div style={{ display: "flex", gap: 16, flexWrap: "wrap" }}>
            {EMPRESAS_DRE.map((emp) => (
              <div key={emp} style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <span style={{ fontSize: 12, fontWeight: 600, color: C.muted, minWidth: 44 }}>{emp}</span>
                <input
                  type="number" step="0.01"
                  value={saldoPrejuizo[emp] ?? ""}
                  onChange={(e) => setSaldoPrejuizo((prev) => ({ ...prev, [emp]: e.target.value }))}
                  style={{ width: 130, padding: "5px 6px", borderRadius: 6, fontSize: 12.5, textAlign: "right", border: `1px solid ${C.border}`, background: C.bg, color: C.text }}
                />
                <button
                  type="button" disabled={salvando === `prejuizo-${emp}`}
                  onClick={() => salvarSaldoPrejuizo(emp)}
                  style={{
                    background: "transparent", border: `1px solid ${C.brand}`, borderRadius: 6,
                    padding: "4px 10px", fontSize: 11, fontWeight: 600, color: C.brand, cursor: "pointer",
                  }}
                >
                  {salvando === `prejuizo-${emp}` ? "Salvando…" : "Salvar"}
                </button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default function DreEmpresaView({ data, ano, C, onRecarregar }) {
  const empresas = useMemo(() => data?.empresas || [], [data]);
  const [empresaSelecionada, setEmpresaSelecionada] = useState(EMPRESAS_DRE[0]);
  const empresaAtual = useMemo(
    () => empresas.find((e) => e.empresa === empresaSelecionada) || empresas[0] || null,
    [empresas, empresaSelecionada]
  );

  const linhas = useMemo(() => empresaAtual?.linhas || [], [empresaAtual]);
  const [colapsados, setColapsados] = useState(() => new Set());
  useEffect(() => {
    setColapsados(new Set(coletarIdsComFilhos(linhas)));
  }, [empresaAtual, linhas]);

  function toggle(id) {
    setColapsados((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  const idsComFilhos = useMemo(() => coletarIdsComFilhos(linhas), [linhas]);
  const prejuizoFiscal = useMemo(() => {
    const linhaImpostos = linhas.find((l) => l.id === "impostos");
    return linhaImpostos?.prejuizo_fiscal || null;
  }, [linhas]);

  if (!empresaAtual) return null;

  const kpis = empresaAtual.kpis;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
      {/* Comparativo entre empresas */}
      <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 14, overflow: "hidden" }}>
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr>
                <th style={thStyle(C, { textAlign: "left" })}>Empresa</th>
                <th style={thStyle(C)}>Faturamento Bruto</th>
                <th style={thStyle(C)}>Receita Líquida</th>
                <th style={thStyle(C)}>Lucro Bruto</th>
                <th style={thStyle(C)}>EBITDA</th>
                <th style={thStyle(C)}>LAIR</th>
                <th style={thStyle(C)}>Resultado Líquido</th>
              </tr>
            </thead>
            <tbody>
              {empresas.map((emp) => (
                <tr
                  key={emp.empresa}
                  onClick={() => setEmpresaSelecionada(emp.empresa)}
                  style={{
                    background: emp.empresa === empresaAtual.empresa ? C.bgActive : "transparent",
                    cursor: "pointer", fontWeight: emp.empresa === empresaAtual.empresa ? 700 : 400,
                  }}
                >
                  <td style={tdStyle(C, { textAlign: "left" })}>{emp.empresa}</td>
                  <td style={tdStyle(C)}>{formatCurrency(emp.kpis.faturamento_bruto.total.planejado)}</td>
                  <td style={tdStyle(C)}>{formatCurrency(emp.kpis.receita_liquida.total.planejado)}</td>
                  <td style={tdStyle(C)}>{formatCurrency(emp.kpis.lucro_bruto.total.planejado)}</td>
                  <td style={tdStyle(C)}>{formatCurrency(emp.kpis.ebitda.total.planejado)}</td>
                  <td style={tdStyle(C)}>{formatCurrency(emp.kpis.lair.total.planejado)}</td>
                  <td style={tdStyle(C, { fontWeight: 700 })}>{formatCurrency(emp.kpis.resultado_liquido.total.planejado)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Seletor de empresa */}
      <div style={{ display: "flex", gap: 8 }}>
        {EMPRESAS_DRE.map((emp) => (
          <button
            key={emp} type="button" onClick={() => setEmpresaSelecionada(emp)}
            style={{
              padding: "8px 18px", borderRadius: 8, fontSize: 13, fontWeight: 700, cursor: "pointer",
              border: `1px solid ${emp === empresaAtual.empresa ? C.brand : C.border}`,
              background: emp === empresaAtual.empresa ? C.brand : "transparent",
              color: emp === empresaAtual.empresa ? "#fff" : C.text,
            }}
          >
            {emp}
          </button>
        ))}
      </div>

      {/* KPIs da empresa selecionada */}
      <div style={{ display: "flex", flexWrap: "wrap", gap: 12 }}>
        <KpiCard label="Faturamento Bruto" valor={formatCurrency(kpis.faturamento_bruto.total.planejado)} C={C} />
        <KpiCard label="Receita Líquida" valor={formatCurrency(kpis.receita_liquida.total.planejado)} C={C} />
        <KpiCard label="Lucro Bruto" valor={formatCurrency(kpis.lucro_bruto.total.planejado)} C={C} />
        <KpiCard label="EBITDA" valor={formatCurrency(kpis.ebitda.total.planejado)} C={C} />
        <KpiCard label="LAIR" valor={formatCurrency(kpis.lair.total.planejado)} C={C} />
        <KpiCard
          label="Resultado Líquido" valor={formatCurrency(kpis.resultado_liquido.total.planejado)}
          sublinha={prejuizoFiscal ? `Saldo Prejuízo Fiscal: ${formatCurrency(prejuizoFiscal.saldo_final[11])}` : undefined}
          C={C}
        />
      </div>

      <PainelRateio ano={ano} C={C} onSalvo={onRecarregar} />

      {/* Botão expandir/recolher */}
      <div style={{ display: "flex", justifyContent: "flex-end" }}>
        <button
          type="button"
          onClick={() => setColapsados(colapsados.size > 0 ? new Set() : new Set(idsComFilhos))}
          style={{
            background: "transparent", border: `1px solid ${C.border}`, borderRadius: 8,
            padding: "6px 12px", fontSize: 11.5, fontWeight: 600, color: C.text, cursor: "pointer",
          }}
        >
          {colapsados.size > 0 ? "Expandir tudo" : "Recolher tudo"}
        </button>
      </div>

      {/* Tabela de DRE da empresa selecionada */}
      <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 14, overflow: "hidden" }}>
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 1400 }}>
            <thead>
              <tr>
                <th style={thStyle(C, { textAlign: "left" })}>{empresaAtual.empresa} · {ano}</th>
                {MESES_ABREV.map((m) => <th key={m} style={thStyle(C, { textAlign: "center" })}>{m}</th>)}
                <th style={thStyle(C, { textAlign: "center" })}>Total</th>
              </tr>
            </thead>
            <tbody>
              {linhas.map((linha) => (
                <EmpresaLinhaRow key={linha.id} linha={linha} nivel={0} colapsados={colapsados} toggle={toggle} C={C} />
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
